<template>
  <div class="w-full">
    <div class="w-full text-gray-600 text-md font-bold pb-2 font-serif">
      {{ $t("order_devices.title") }}
    </div>
    <!-- demo feedback: creating devices here also sends their documentation -->
    <p class="text-sm italic text-black px-2 pb-2">{{ $t("order_devices.auto_send_hint") }}</p>
    <p v-if="sendingDocumentation" class="text-sm text-gray-500 px-2 pb-2">{{ $t("order_devices.documentation_sending") }}</p>

    <p v-if="loading" class="text-sm text-gray-500 px-2 py-2">{{ $t("order_devices.loading") }}</p>
    <p v-else-if="loadFailed" class="text-sm text-red-600 px-2 py-2">{{ $t("order_devices.load_failed") }}</p>

    <template v-else-if="detection">
      <!-- Target: customer -> business -->
      <p class="text-sm text-gray-800 px-2 pb-2">
        {{ $t("order_devices.target") }}:
        <span class="font-semibold">{{ detection.customer.name || detection.customer.customer_id }}</span>
        <span class="mx-1">→</span>
        <span class="font-semibold">{{ detection.unit.name || detection.unit.unit_id }}</span>
      </p>

      <p v-if="!resolvedLines.length" class="text-sm text-gray-500 px-2 py-2">
        {{ $t("order_devices.no_device_lines") }}
      </p>

      <!-- Device lines -->
      <div v-else class="w-full px-2 overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-600 border-b border-gray-300">
              <th class="py-1 pr-2 font-semibold">{{ $t("order_devices.col_product") }}</th>
              <th class="py-1 pr-2 font-semibold text-center">{{ $t("order_devices.col_qty") }}</th>
              <th class="py-1 pr-2 font-semibold text-center">{{ $t("order_devices.col_created") }}</th>
              <th class="py-1 font-semibold">{{ $t("order_devices.col_status") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in resolvedLines" :key="line.line_key" class="border-b border-gray-100">
              <td class="py-1.5 pr-2 text-gray-800">{{ line.display_name }}</td>
              <td class="py-1.5 pr-2 text-center text-gray-800">{{ line.qty }}</td>
              <td class="py-1.5 pr-2 text-center text-gray-800">{{ line.created_count }}</td>
              <td class="py-1.5">
                <span class="rounded-full px-2 py-0.5 text-xs font-semibold" :class="lineBadgeClass(line)">
                  {{ lineBadgeLabel(line) }}
                </span>
                <span v-if="lineError(line)" class="block text-xs text-red-600 mt-1">{{ lineError(line) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Non-device lines, shown so nothing looks silently skipped -->
      <div v-if="detection.ignored_lines.length" class="w-full px-2 pt-2">
        <p class="text-xs text-gray-500">
          {{ $t("order_devices.not_devices") }}:
          <span v-for="(line, i) in detection.ignored_lines" :key="line.line_key || i">
            {{ line.qty }} × {{ line.display_name }}<span v-if="i < detection.ignored_lines.length - 1">, </span>
          </span>
        </p>
      </div>

      <p v-if="hasUnmatched" class="text-xs text-red-600 px-2 pt-2">
        {{ $t("order_devices.unmatched_note") }}
      </p>

      <!-- Create / retry / done -->
      <div class="w-full px-2 pt-3" v-if="resolvedLines.length">
        <template v-if="confirmingTopUp">
          <p class="text-sm text-gray-800 pb-2">
            {{ $t("order_devices.confirm_topup", { created: totals.created, remaining: totals.remaining }) }}
          </p>
          <button class="btn-green px-3 py-2 mr-2" @click="openDetailsModal">
            {{ $t("order_devices.confirm_yes") }}
          </button>
          <button class="btn-blue-outline px-3 py-2" @click="confirmingTopUp = false">
            {{ $t("order_devices.confirm_no") }}
          </button>
        </template>
        <template v-else>
          <button
            v-if="totals.remaining > 0"
            class="btn-green px-3 py-2"
            :disabled="tooManyDevices"
            :class="{ 'opacity-50 cursor-not-allowed': tooManyDevices }"
            @click="onCreateClick"
          >
            {{ hadFailures
              ? $t("order_devices.retry_button", { count: totals.remaining })
              : $t("order_devices.create_button", { count: totals.remaining }) }}
          </button>
          <span v-else class="text-sm font-semibold text-green-700">
            {{ $t("order_devices.all_created") }}
          </span>
        </template>
        <p v-if="tooManyDevices" class="text-xs text-red-600 pt-2">
          {{ $t("order_devices.too_many_devices", { limit: maxDevicesPerRun }) }}
        </p>
        <p v-if="hadFailures" class="text-xs text-red-600 pt-2">{{ $t("order_devices.partial_failure") }}</p>
      </div>

      <!-- The devices are created from this modal — opening it writes nothing,
           so cancelling it creates nothing. -->
      <OrderDeviceDetailsModal
        mode="create"
        modalName="modal-automatic-device-create"
        :pendingDevices="pendingSlots"
        :orderId="orderId"
        :agreementNumber="detection.agreement_number || ''"
        :alarmCentralId="(detection.alarm_central && detection.alarm_central._id) || ''"
        :externalResponder="detection.external_responder || {}"
        :unitName="detection.unit.name || ''"
        :people="detection.people || []"
        @devices-created="onDevicesCreated"
        @completed="goToBusiness"
        @closed="fetchDetection"
      />
    </template>
  </div>
</template>

<script>
import { resolveDeviceType } from "@/utils/deviceTypes";
import OrderDeviceDetailsModal from "@/components/modal/automatic_device_creation_details";

// Keep in sync with MAX_DEVICES_PER_RUN on the server (fallback only — the
// detection response carries the server's value).
const DEFAULT_MAX_DEVICES_PER_RUN = 100;

// Device types that have no alarm documentation to send (same list the device
// window and the bulk update use).
const TYPES_WITHOUT_DOCUMENTATION = ["SRT330", "SRT430", "SRT334", "TWIG Beacon", "Anchor Beacon"];

// How many documents one email request carries; the number of devices itself
// is not capped.
const DOCUMENTS_PER_EMAIL_REQUEST = 20;

export default {
  name: "AutomaticDeviceCreationPanel",

  components: {
    OrderDeviceDetailsModal,
  },

  props: {
    orderId: {
      type: String,
      required: true,
    },
    // sellers/resellers, so the documentation PDF carries the right sender
    sellers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      loadFailed: false,
      confirmingTopUp: false,
      detection: null,
      // line_key -> error message from the last creation run
      lastRunErrors: {},
      hadFailures: false,
      // the alarm documentation is being generated and emailed
      sendingDocumentation: false,
    };
  },

  computed: {
    // The frontend is the single source of truth for device types
    // (@/utils/deviceTypes): the server reports which catalog product each
    // line is, and the mapping to a canonical device type happens here.
    resolvedLines() {
      if (!this.detection) return [];
      return this.detection.lines.map((line) => {
        if (!line.matched) return { ...line, device_type: null };
        // Always the catalog type, never the name: the name is edited by hand
        // ("SRT405i - personalarm med ...", "SRT334i"), the type never changes.
        const deviceType = resolveDeviceType(line.product_type, line.category);
        if (deviceType) return { ...line, device_type: deviceType };
        return { ...line, device_type: null, matched: false, unmatched_reason: "unknown_device_type" };
      });
    },
    totals() {
      return this.resolvedLines.reduce(
        (acc, line) => {
          acc.created += line.created_count;
          if (line.matched) {
            acc.orderable += line.qty;
            acc.remaining += Math.max(0, line.qty - line.created_count);
          }
          return acc;
        },
        { orderable: 0, created: 0, remaining: 0 }
      );
    },
    hasUnmatched() {
      return this.resolvedLines.some((line) => !line.matched);
    },
    // The missing order-line slots: what the details modal will create.
    pendingSlots() {
      const slots = [];
      this.resolvedLines.forEach((line) => {
        if (!line.matched) return;
        const existing = new Set(line.created_indexes);
        for (let index = 0; index < line.qty; index++) {
          if (!existing.has(index)) slots.push({ line_key: line.line_key, index, device_type: line.device_type });
        }
      });
      return slots;
    },
    maxDevicesPerRun() {
      return (this.detection && this.detection.max_devices_per_run) || DEFAULT_MAX_DEVICES_PER_RUN;
    },
    // Safety cap, enforced on the server too: block absurd creation runs.
    tooManyDevices() {
      return this.pendingSlots.length > this.maxDevicesPerRun;
    },
  },

  mounted() {
    this.fetchDetection();
  },

  methods: {
    async fetchDetection() {
      this.loading = true;
      this.loadFailed = false;
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/order-alarm/${this.orderId}/devices`
        );
        this.detection = response.data;
      } catch (error) {
        this.loadFailed = true;
        this.handleError(error, this.$t("order_devices.load_failed"));
      } finally {
        this.loading = false;
      }
    },

    onCreateClick() {
      // Devices already exist (retry after failure, or qty raised after
      // creation): require an explicit confirmation before topping up.
      if (this.totals.created > 0 && !this.hadFailures) {
        this.confirmingTopUp = true;
        return;
      }
      this.openDetailsModal();
    },

    // Nothing is written here: the modal creates the devices when the user
    // confirms with its footer button, so aborting the modal creates nothing.
    openDetailsModal() {
      this.confirmingTopUp = false;
      this.$modal.show("modal-automatic-device-create");
    },

    // Creation result reported by the modal: keep the per-line errors for
    // the panel table and the retry state.
    onDevicesCreated(data, sendToCentral) {
      this.lastRunErrors = {};
      data.results.forEach((result) => {
        if (result.error) this.lastRunErrors[result.line_key] = result.error;
      });
      this.hadFailures = data.partial;

      // the documentation is emailed to the alarm central only when the user
      // chose "create and send to alarm central"; "create devices" just creates
      if (sendToCentral) this.sendDocumentationForCreated(data.created_alarms || []);
    },

    // Send the alarm documentation for the devices this run created, one email
    // per device, grouped by the alarm central each device answers to. The
    // documentation PDF is built here in the browser, exactly as the device
    // window and the bulk update build it.
    //
    // Devices with no alarm central, and device types that have no
    // documentation, are skipped - the panel says how many.
    async sendDocumentationForCreated(alarms) {
      const sendable = alarms.filter(
        (alarm) => alarm.alarm_central && alarm.alarm_central !== "-" && !TYPES_WITHOUT_DOCUMENTATION.includes(alarm.type)
      );
      if (sendable.length === 0) {
        if (alarms.length > 0) this.handleError(null, this.$t("order_devices.documentation_not_sent", { count: alarms.length }));
        return;
      }

      this.sendingDocumentation = true;
      try {
        const customerName = this.detection && this.detection.customer ? this.detection.customer.name : "";
        const groups = {}; // central id -> [{ alarm_id, blob }]

        for (const alarm of sendable) {
          // nothing to compare against on a brand-new device: no highlighting
          const doc = await this.generateAlarmPlanPDF({
            alarm,
            customerName,
            sellers: this.sellers,
            oldAlarm: alarm,
            highlight: false,
            // SEC-337: the IMSI row belongs on Securitas Sverige AB documents
            showImsiNumber: this.isImsiCentral(alarm.alarm_central),
            showDirectDispatch: Boolean(alarm.external_contractor && alarm.external_contractor.name),
            unit: this.detection ? this.detection.unit : null,
            customer: this.detection ? this.detection.customer : null,
          });
          const central = alarm.alarm_central;
          (groups[central] = groups[central] || []).push({ alarm_id: String(alarm._id), blob: doc.output("blob") });
        }

        let failed = 0;
        for (const central of Object.keys(groups)) {
          failed += await this.postDocumentationBatch(central, groups[central]);
        }

        const skipped = alarms.length - sendable.length;
        if (failed > 0) this.handleError(null, this.$t("order_devices.documentation_failed", { count: failed }));
        else this.handleSuccess(this.$t("order_devices.documentation_sent", { count: sendable.length }));
        if (skipped > 0) this.handleError(null, this.$t("order_devices.documentation_not_sent", { count: skipped }));
      } catch (error) {
        this.handleError(error, this.$t("order_devices.documentation_failed", { count: sendable.length }));
      } finally {
        this.sendingDocumentation = false;
      }
    },

    // SEC-337: is this the order's centre, and is that Securitas Sverige AB?
    isImsiCentral(centralId) {
      const central = this.detection && this.detection.alarm_central;
      return !!central && central._id === String(centralId || "") && central.name === "Securitas Sverige AB";
    },

    // One central's documents, a batch at a time so a large order never sits
    // in a single request. Returns how many documents could not be sent.
    async postDocumentationBatch(central, pdfs) {
      let failed = 0;
      for (let from = 0; from < pdfs.length; from += DOCUMENTS_PER_EMAIL_REQUEST) {
        const batch = pdfs.slice(from, from + DOCUMENTS_PER_EMAIL_REQUEST);
        const formData = new FormData();
        const ids = [];
        batch.forEach((pdf) => {
          formData.append("pdfs", pdf.blob, `${pdf.alarm_id}.pdf`);
          ids.push(pdf.alarm_id);
        });
        formData.append("alarm_ids", JSON.stringify(ids));
        // a device that was just created has never been sent before, so the
        // central is told this is new documentation
        formData.append("is_new", "true");

        try {
          const response = await this.axios.post(
            `${process.env.VUE_APP_SERVER_URL}/alarm-center/${central}/email/bulk`,
            formData,
            { headers: { "X-Access-Token": this.$store.state.token, "Content-Type": "multipart/form-data" } }
          );
          const data = response.data || {};
          failed += Array.isArray(data.failed_alarm_ids) ? data.failed_alarm_ids.length : (data.error ? batch.length : 0);
        } catch (error) {
          failed += batch.length;
        }
      }
      return failed;
    },

    // Creation completed in the modal: open the business, where the created
    // devices are listed, in a new tab so the order window stays open here.
    // The tab opens only after the save requests, so a popup blocker may
    // refuse it - then the order window closes and the business opens in
    // this tab instead.
    goToBusiness() {
      const { customer, unit } = this.detection;
      const route = {
        name: "alarms",
        params: { type: "customer", customer_id: customer.customer_id, unit_id: unit.unit_id, id: "-" },
      };
      const tab = window.open(this.$router.resolve(route).href, "_blank");
      if (tab) {
        tab.opener = null;
        return;
      }
      this.$modal.hide("modal-admin-order-alarm");
      this.$router.push(route).catch(() => {}); // already on the business view
    },

    lineBadgeLabel(line) {
      if (!line.matched) {
        const key = `order_devices.status_${line.unmatched_reason}`;
        return this.$te(key) ? this.$t(key) : this.$t("order_devices.status_unmatched");
      }
      if (line.created_count > line.qty) return this.$t("order_devices.status_over_created");
      if (line.created_count >= line.qty) return this.$t("order_devices.status_done");
      if (line.created_count > 0) return this.$t("order_devices.status_partial");
      return this.$t("order_devices.status_none");
    },

    lineBadgeClass(line) {
      if (!line.matched) return "bg-red-100 text-red-800";
      if (line.created_count > line.qty) return "bg-yellow-100 text-yellow-800";
      if (line.created_count >= line.qty) return "bg-green-100 text-green-800";
      if (line.created_count > 0) return "bg-yellow-100 text-yellow-800";
      return "bg-gray-200 text-gray-700";
    },

    lineError(line) {
      return this.lastRunErrors[line.line_key] || "";
    },
  },
};
</script>
